import {postOnNextEvent} from "@components/Booking/EnterEmail/events";
import {useSendPasswordlessLoginMutation} from "@components/Booking/EnterEmail/graphql/__generated__/send-passwordless-login.type";
import {setEmail} from "@page-containers/booking/utils/emailStorage";
import {useRouter} from "next/router";
import {useTranslation} from "@i18n/client";
import {useCallback} from "react";
import {I18nNamespace} from "@i18n";
import {CustomCategory, EventCategory, EventType} from "src/utils/analytics/constants";
import {getFlowId} from "src/utils/getFlowId";

import {MonolithLocationFields} from "../../_services/graphql/queries/location";
import {isEmailAvailable} from "../../_services/users";
import {
  buildBookingLink,
  parseBookingQuery,
  useParsedBookingQuery,
} from "../../page-containers/booking/utils/bookingQuery";
import {queueEvent} from "../../utils/analytics/eventQueue";
import {usePostBookingProgressMutation} from "@page-containers/booking/utils/bookingProgress/useBookingProgressMutation";

type EventData = {
  email: string;
  typeId: EventType;
};

export const useEmailEnteredHandler = (
  viewId: string,
  apptReasonId?: string | null,
  apptReasonSlug?: string | null,
  location?: MonolithLocationFields | null,
  time?: number | null,
  dropoutVisitorId?: string,
) => {
  const router = useRouter();
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const [sendPasswordlessLoginMutation] = useSendPasswordlessLoginMutation();
  const locationId = location?.id;
  const flowId = getFlowId();
  const nextButtonText = t("Next", {ns: "web-scheduling"});
  const query = useParsedBookingQuery();

  const emitEvent = useCallback(
    ({email, typeId}: EventData) => {
      postOnNextEvent({
        typeId,
        email,
        viewId,
        nextButtonText,
      });
    },
    [nextButtonText, viewId],
  );

  const [postBookingProgress] = usePostBookingProgressMutation(dropoutVisitorId);

  return useCallback(
    async (email: string) => {
      if (!apptReasonId || !apptReasonSlug || !locationId || !time) {
        return null;
      }

      const isAvailable = await isEmailAvailable(email);

      if (isAvailable) {
        setEmail(email);
        emitEvent({email, typeId: EventType.EmailCheckClicked});
        postBookingProgress({
          patientInfo: {
            email,
          },
        });
        return null;
      }

      const redirectUrl = buildBookingLink({
        ...query,
        confirmationEmailSent: undefined,
      });
      const {errors} = await sendPasswordlessLoginMutation({
        variables: {
          input: {
            email,
            maxTtlDesired: "PT168H",
            redirectUrl,
            flowId: query.flowId ?? "",
            isResend: false,
          },
        },
      });
      await router.replace(
        buildBookingLink({
          ...parseBookingQuery(router.query),
          confirmationEmailSent: true,
        }),
      );
      queueEvent({
        category: EventCategory.Custom,
        extraData: {
          category: CustomCategory.AppointmentScheduling,
          flowId,
          emailEntered: email,
          success: (errors?.length ?? 0) === 0,
        },
        typeId: EventType.LoginLinkTriggered,
      });
      return email;
    },
    [
      apptReasonId,
      apptReasonSlug,
      locationId,
      time,
      flowId,
      emitEvent,
      query,
      router,
      sendPasswordlessLoginMutation,
      postBookingProgress,
    ],
  );
};
